'use client';

import { REGIONS } from '@/utils/regions';
import safeWindow from '@/utils/safeWindow';

/** Non-empty locale prefixes (e.g. '/uk', '/de') for matching. */
const LOCALE_PREFIXES = REGIONS.map((r) => r.prefix).filter(Boolean);

/**
 * Pure helper — given the current pathname and a target path, return the
 * locale-prefixed version of the path.
 *
 * Exported so tests (and LocaleLink) can use it without a React context.
 */
export function prefixPath(currentPathname, path) {
  if (typeof path !== 'string' || !path.startsWith('/')) return path;

  // Protocol-relative URL (e.g. //example.com) — pass through unchanged.
  if (path.startsWith('//')) return path;

  // Extract pathname portion (before ? and #) for the already-prefixed check.
  const pathOnly = path.split('?')[0].split('#')[0];

  // Already has a known locale prefix → leave it alone.
  const alreadyPrefixed = LOCALE_PREFIXES.some(
    (p) => pathOnly === p || pathOnly.startsWith(p + '/'),
  );
  if (alreadyPrefixed) return path;

  // Detect the locale prefix of the current route.
  const currentPrefix = LOCALE_PREFIXES.find(
    (p) => currentPathname === p || currentPathname.startsWith(p + '/'),
  );

  if (!currentPrefix) return path;

  return currentPrefix + path;
}

/**
 * Hook that returns a function to prefix paths with the current locale.
 *
 * Uses window.location.pathname (the real browser URL) instead of
 * usePathname(), because the middleware rewrites /de/net-worth/... to
 * /net-worth/... for internal routing — usePathname() returns the
 * rewritten path which has no locale prefix to detect.
 *
 * Usage:
 *   const localePath = useLocalePath();
 *   router.push(localePath('/signin?returnUrl=...'));
 */
export default function useLocalePath() {
  return (path) => prefixPath(safeWindow.location.pathname, path);
}
